import { ToastOptions } from "react-toastify";
import { ObjectToBeDefined } from "../types/types";

export const getDayofTheWeek = (day: string) => {
  switch (day) {
    case "Mon":
      return "Monday";
    case "Tue":
      return "Tuesday";
    case "Wed":
      return "Wednesday";
    case "Thu":
      return "Thursday";
    case "Fri":
      return "Friday";
    case "Sat":
      return "Saturday";
    case "Sun":
      return "Sunday";
    default:
      return "";
  }
};

export const getShortDayofTheWeek = (day: string) => {
  switch (day) {
    case "Monday":
      return "Mon";
    case "Tuesday":
      return "Tue";
    case "Wednesday":
      return "Wed";
    case "Thursday":
      return "Thur";
    case "Friday":
      return "Fri";
    case "Saturday":
      return "Sat";
    case "Sunday":
      return "Sun";
    default:
      return day;
  }
};

export const getDayofTheWeekValue = (day: string | ObjectToBeDefined) => {
  if (typeof day === "object") {
    return day.value;
  }
  switch (day) {
    case "Monday":
      return "0";
    case "Tuesday":
      return "1";
    case "Wednesday":
      return "2";
    case "Thursday":
      return "3";
    case "Friday":
      return "4";
    case "Saturday":
      return "5";
    case "Sunday":
      return "6";
    default:
      throw new Error("Invalid day");
  }
};

export const minuteOptions = (increment: number, length: number, start = 0) => {
  const result: { value: string; label: string }[] = [];
  let cur = start;
  for (let i = 0; i < length; i++) {
    result.push({
      value: `${cur * 60}`,
      label: `${cur} min`,
    });
    cur += increment;
  }
  return result;
};

export const createMinuteOptions = (
  arr: number[],
  emptyLabel: string,
  emptyValue: string,
) => {
  const emptyOption = {
    value: emptyValue,
    label: emptyLabel,
  };
  const options = arr.map((nu) => {
    return {
      value: `${nu * 60}`,
      label: `${nu} min`,
    };
  });
  return [emptyOption, ...options];
};

export const convertToSeconds = (value: string, option: string) => {
  const time = parseInt(value, 10);
  switch (option) {
    case "minute":
    case "minutes":
      return time * 60;
    case "hour":
    case "hours":
      return time * 3600;
    case "day":
    case "days":
      return time * 86400;
    case "week":
    case "weeks":
      return time * 604800;
    case "month":
    case "months":
      return time * 2592000;
    default:
      return value;
  }
};

export const mixedScheduleOptions = [
  {
    value: "0",
    label: "0 mins before",
  },
  {
    value: "225",
    label: "15 mins before",
  },
  {
    value: "900",
    label: "30 mins before",
  },
  {
    value: "2700",
    label: "45 mins before",
  },
  {
    value: "3600",
    label: "1 hour before",
  },
  {
    value: "7200",
    label: "2 hours before",
  },
  {
    value: "10800",
    label: "3 hours before",
  },
  {
    value: "14400",
    label: "4 hours before",
  },
  {
    value: "21600",
    label: "6 hours before",
  },
  {
    value: "28800",
    label: "8 hours before",
  },
  {
    value: "43200",
    label: "12 hours before",
  },
  {
    value: "57600",
    label: "16 hours before",
  },
  {
    value: "86400",
    label: "1 day before",
  },
  {
    value: "172800",
    label: "2 days before",
  },
];

export const formatToUSD = (value: number, currency = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatToPercent = (value: number) => `${value.toFixed()}%`;

/**
 * Converts seconds to the largest whole unit consisting of minutes, hours, or days
 * @param {number} secs
 * @param {object} defaultValues { value: number, unit: 'minute' | 'hour' | 'day' }
 * @returns {object} { value: number, unit: 'minute' | 'hour' | 'day' }
 */
export const convertSecondsToLargestWholeUnit = (secs: number | string) => {
  const hourInMins = 60;
  const dayInMins = hourInMins * 24;

  const secsValue = typeof secs === "string" ? parseInt(secs, 10) : secs;
  const mins = secsValue / 60;
  if (mins % dayInMins === 0) {
    const value = mins / dayInMins;
    return {
      value,
      unit: value > 1 ? "days" : "day",
    };
  }
  if (mins % hourInMins === 0) {
    const value = mins / hourInMins;
    return {
      value,
      unit: value > 1 ? "hours" : "hour",
    };
  }
  return {
    value: mins,
    unit: mins > 1 ? "minutes" : "minute",
  };
};

/**
 * Extract the error message
 * @param err
 * @returns message
 */
export const extractError = (err: unknown): string => {
  if (typeof err === "string") {
    return err;
  } else if (typeof err === "object" && err !== null) {
    const errObj = err as { [key: string]: unknown };
    if ("message" in errObj) {
      return errObj["message"] as string;
    } else if ("error" in errObj && typeof errObj["error"] === "string") {
      return errObj["error"];
    } else if ("data" in errObj) {
      const data = errObj["data"] as { [key: string]: unknown };
      if ("message" in data) {
        return data["message"] as string;
      } else if ("error" in data && typeof data["error"] === "string") {
        return data["error"];
      } else if (
        "error" in data &&
        typeof data["error"] === "object" &&
        data["error"] !== null &&
        "message" in (data["error"] as { [key: string]: unknown }) &&
        typeof (data["error"] as { [key: string]: unknown })["message"] ===
          "string"
      ) {
        return (data["error"] as { [key: string]: unknown })[
          "message"
        ] as string;
      }
    }
  } else if (Array.isArray(err)) {
    const first = err[0];
    if (
      typeof first === "object" &&
      first !== null &&
      "errors" in first &&
      "issues" in (first["errors"] as { [key: string]: unknown })
    ) {
      const issues = first["errors"]["issues"] as { [key: string]: unknown }[];
      if (Array.isArray(issues) && issues.length > 0) {
        return `${(issues[0] as { [key: string]: unknown })["message"]} (${
          (issues[0] as { [key: string]: unknown })["path"]
        })`;
      }
    }
  }
  return "Something went wrong.";
};

export const defaultToastOptions: ToastOptions = {
  position: "bottom-center",
  autoClose: 5000,
  hideProgressBar: true,
  closeOnClick: true,
  pauseOnHover: true,
  progress: undefined,
};

type Features = {
  [key: string]: boolean;
};

export const sidebarFeatures = (input: Features) => {
  return {
    ...input,
    legacyAppointments: input.appointments === true ? false : true,
  };
};

export function getInitials(...parts: (string | undefined | null)[]): string {
  return parts
    .filter(Boolean)
    .map((item) => item![0])
    .join("");
}

export function joinStrings(...parts: (string | undefined | null)[]): string {
  return parts.filter(Boolean).join(" ");
}

export function capitalizeFirstLetter(str: string) {
  if (!str || str.length === 0) return "--";

  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function getCompleteName(
  ...parts: (string | null | undefined)[]
): string {
  return parts.filter(Boolean).join(" ");
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function toFixedConditional(num: number): string {
  const fixedStr = num.toFixed(2);
  return fixedStr.endsWith(".00") ? fixedStr.slice(0, -3) : fixedStr;
}
